// A client-side unique id, without a dependency.
//
// Two things need one and neither can ask the server for it: a page element,
// which is created and reordered entirely inside one form and only reaches the
// API as part of the whole array; and a row of a `list` param, which the list
// widget keys on to tell drag-reorder from re-render.
//
// `uuid` is the obvious package and is what the archived project used, but it
// is 4 kB and a build step for one call site each. crypto.randomUUID is in
// every browser this app supports — it is only unavailable in insecure contexts,
// and the app is served over HTTPS everywhere including local. The fallbacks
// exist for jsdom, whose crypto shim varies by version, rather than for any
// browser.
export const uid = () => {
  if (typeof crypto !== "undefined") {
    if (typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }

    if (typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      // Version 4, variant 1 — the bits a v4 uuid is required to pin.
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;

      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        hex.slice(12, 16),
        hex.slice(16, 20),
        hex.slice(20),
      ].join("-");
    }
  }

  // Not a uuid and not claiming to be. Reached only where neither crypto path
  // exists, where the ids never leave the process.
  return `id-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
};
